import type { EmailOtpType } from '@supabase/supabase-js';
import { useEffect, useState } from 'react';
import { Navigate } from 'react-router';
import { CenteredPage } from '../app/CenteredPage';
import { ButtonLink } from '../components/Button';
import { Paper } from '../components/Paper';
import { EmptyState, Loading } from '../components/States';
import { useSession } from '../features/auth/session';
import { supabase } from '../lib/supabase';

// Links are single-use, so verify each token once even if the effect runs twice.
const verifications = new Map<string, Promise<boolean>>();

function verifyEmailLink(tokenHash: string, type: EmailOtpType) {
  let pending = verifications.get(tokenHash);
  if (!pending) {
    pending = supabase.auth.verifyOtp({ token_hash: tokenHash, type }).then(({ error }) => !error);
    verifications.set(tokenHash, pending);
  }
  return pending;
}

/**
 * Landing page for sign-in emails and OAuth.
 *
 * Emails link here with `?token_hash=…&type=email`, which we verify directly, so
 * the link works in any browser (a private tab, another device, the home-screen
 * app). OAuth, and emails sent before that change, arrive with `?code=…`, which
 * supabase-js exchanges on load; that needs the browser that started sign-in.
 */
export function AuthCallback() {
  const { session, loading } = useSession();
  const [timedOut, setTimedOut] = useState(false);
  const [linkFailed, setLinkFailed] = useState(false);
  const params = new URLSearchParams(window.location.search);
  const tokenHash = params.get('token_hash');
  const type = params.get('type') as EmailOtpType | null;
  const urlError =
    params.get('error_description') ?? new URLSearchParams(window.location.hash.slice(1)).get('error_description');

  useEffect(() => {
    if (!tokenHash || !type) return;
    let cancelled = false;
    verifyEmailLink(tokenHash, type).then((ok) => {
      if (!cancelled && !ok) setLinkFailed(true);
    });
    return () => {
      cancelled = true;
    };
  }, [tokenHash, type]);

  useEffect(() => {
    const timer = setTimeout(() => setTimedOut(true), 8000);
    return () => clearTimeout(timer);
  }, []);

  if (session) return <Navigate to="/" replace />;

  if (urlError || linkFailed || (timedOut && !loading)) {
    return (
      <CenteredPage>
        <Paper>
          <EmptyState
            title="That sign-in link didn't work"
            actions={
              <ButtonLink to="/welcome" variant="primary">
                Get a new link
              </ButtonLink>
            }
          >
            Links expire after an hour and work once. Ask for a new one and open the newest email.
          </EmptyState>
        </Paper>
      </CenteredPage>
    );
  }

  return (
    <CenteredPage>
      <Loading label="Signing you in" />
    </CenteredPage>
  );
}
